import axios from "axios";
import { FileConfig, FileKind } from "../components/Menu/uploadFileConfig";
import { uploadAudioToS3 } from "../utils/audioUtils";

// Saving files straight to the Files library, with no report. Mirrors the first
// half of a report upload (presign, PUT to S3) but talks to /api/files, so the
// server creates a library row instead of a case, and the transcription
// Lambdas report back to that row.

// The Files page listens for this to refresh after a save.
export const FILES_CHANGED_EVENT = "pnai:files-changed";

// Rows whose bytes are uploading from THIS tab right now. A "waiting" row that
// is not in here (a leftover from before uploads were reordered, a closed tab)
// was interrupted and will never finish, so the Files list shows it as failed
// instead of waiting forever.
const activeUploads = new Set<string>();
export const isUploadActive = (id: string) => activeUploads.has(id);

const SPREADSHEET_EXT = /\.(xlsx|xls|csv|tsv)$/i;

export function kindForConfig(config: FileConfig): FileKind {
  if (config.mediaType === "audio") return "audio";
  if (config.mediaType === "video") return "video";
  if (config.mediaType === "image") return "image";
  return SPREADSHEET_EXT.test(config.file.name) ? "spreadsheet" : "document";
}

const humanSize = (bytes: number) =>
  bytes >= 1024 * 1024 * 1024
    ? `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
    : `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

async function saveOne(
  config: FileConfig,
  source: "upload" | "recording",
  onProgress: (pct: number) => void,
  signal?: AbortSignal,
): Promise<{ id: string; status: string }> {
  const { file } = config;
  const kind = kindForConfig(config);
  const isAudio = kind === "audio" || kind === "video";

  // A chosen name keeps the file's extension (the kind is decided from it).
  const ext = file.name.includes(".") ? `.${file.name.split(".").pop()}` : "";
  const typed = config.displayName?.trim();
  const savedName = typed ? (typed.toLowerCase().endsWith(ext.toLowerCase()) ? typed : `${typed}${ext}`) : file.name;

  // Upload first, create the Files row after. An upload that is interrupted
  // (refresh, closed tab, network) then leaves nothing behind in the list.
  const presign = await axios.post<{ uploadURL: string; key: string }>("/api/get-upload-url", {
    fileName: file.name,
    fileType: file.type,
    // "Other" goes to the general bucket, like narrative audio.
    audioType: isAudio && !config.libraryOther ? config.audioType : undefined,
    uploadType: isAudio ? undefined : "case-document",
  }, { signal });
  await uploadAudioToS3(file, presign.data.uploadURL, (pct) => onProgress(pct), signal);

  const created = await axios.post<{ file: { id: string } }>("/api/files", {
    fileName: savedName,
    fileSize: humanSize(file.size),
    fileType: file.type || undefined,
    kind,
    audioType: isAudio ? (config.libraryOther ? "other" : config.audioType) : undefined,
    context: config.additionalContext || undefined,
    speakers: isAudio && !config.libraryOther && config.audioType === "interaction" && config.speakers.length ? config.speakers : undefined,
    languages: isAudio && config.languages.length ? config.languages : undefined,
    keepVideo: kind === "video" ? config.keepVideo : undefined,
    source,
  });
  const id = created.data.file.id;
  activeUploads.add(id);
  try {
    const recorded = await axios.post<{ file?: { status?: string } }>(`/api/files/${id}/uploaded`, { key: presign.data.key });
    return { id, status: recorded.data?.file?.status || "processing" };
  } catch (error) {
    // The object is in S3 but the row could not be finished: don't leave a
    // half-created row behind.
    await axios.delete(`/api/files/library/${id}`).catch(() => {});
    throw error;
  } finally {
    activeUploads.delete(id);
  }
}

// ---- Upload queue ------------------------------------------------------------
// A tiny store (not React state) so uploads started from anywhere, the Files page
// or the recorder in the sidebar, feed one tray and keep running while the
// officer moves around the app. Same shape as the Investigations uploader.

export type LibraryUploadItem = {
  id: string;
  name: string;
  // "processing": uploaded, and the server is transcribing or reading it.
  status: "queued" | "uploading" | "processing" | "done" | "error";
  // The Files row, once created (what the processing poll follows).
  fileId?: string;
  progress: number;
  error?: string;
  // What happens next on the server, shown on a finished item.
  after: string;
};

let items: LibraryUploadItem[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const setItems = (next: LibraryUploadItem[]) => {
  items = next;
  emit();
};
const patch = (id: string, p: Partial<LibraryUploadItem>) => setItems(items.map((i) => (i.id === id ? { ...i, ...p } : i)));

export const subscribeLibraryUploads = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
export const getLibraryUploads = () => items;
export const clearFinishedLibraryUploads = () =>
  setItems(items.filter((i) => i.status === "queued" || i.status === "uploading" || i.status === "processing"));

// Tests only: forget every item, queued job, and timer.
export function resetLibraryUploadsForTests() {
  items = [];
  pending.length = 0;
  running = false;
  controllers.forEach((c) => c.abort());
  controllers.clear();
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  emit();
}

// Leaving mid-upload loses the file; ask first, like report uploads do.
const warnBeforeLeaving = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = "";
  return "";
};

// ---- After the upload: follow server-side processing ------------------------

const POLL_MS = 6000;
let pollTimer: ReturnType<typeof setInterval> | null = null;

// One look at every file still being processed. Exported for tests; the timer
// below calls it.
export async function checkProcessingUploads(): Promise<void> {
  const watching = items.filter((i) => i.status === "processing" && i.fileId);
  await Promise.all(
    watching.map(async (i) => {
      try {
        const res = await axios.get<{ file: { status: string; error?: string } }>(`/api/files/library/${i.fileId}`);
        const f = res.data.file;
        if (f.status === "completed") patch(i.id, { status: "done", after: "ready" });
        else if (f.status === "failed") patch(i.id, { status: "error", error: f.error || "Processing failed" });
      } catch (error: any) {
        // Deleted from Files while processing, or the row is gone.
        if (error?.response?.status === 404) patch(i.id, { status: "error", error: "This file was removed from Files." });
        // Any other error is a blip: look again next time.
      }
    }),
  );
  if (!items.some((i) => i.status === "processing") && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  window.dispatchEvent(new Event(FILES_CHANGED_EVENT));
}

function startProcessingPoll() {
  if (pollTimer) return;
  pollTimer = setInterval(() => void checkProcessingUploads(), POLL_MS);
}

const afterLabel = (config: FileConfig) => {
  const kind = kindForConfig(config);
  if (kind === "audio" || kind === "video") return config.libraryOther ? "saved" : "transcribing";
  return "reading";
};

type Job = { itemId: string; config: FileConfig; source: "upload" | "recording" };
const pending: Job[] = [];
let running = false;
// The file currently uploading can be stopped through its controller.
const controllers = new Map<string, AbortController>();

// The X on a file in the tray. A waiting file is just dropped from the queue; an
// uploading one has its transfer aborted (an unfinished S3 upload leaves no
// object, and the Files row is only created after the bytes land, so nothing is
// left behind either way). Files already past the upload step are not affected.
export function cancelLibraryUpload(id: string): void {
  const item = items.find((i) => i.id === id);
  if (!item || (item.status !== "queued" && item.status !== "uploading")) return;
  const queuedAt = pending.findIndex((j) => j.itemId === id);
  if (queuedAt >= 0) pending.splice(queuedAt, 1);
  controllers.get(id)?.abort();
  setItems(items.filter((i) => i.id !== id));
}

async function pump() {
  if (running) return;
  running = true;
  window.addEventListener("beforeunload", warnBeforeLeaving);
  try {
    while (pending.length) {
      const job = pending.shift()!;
      patch(job.itemId, { status: "uploading", progress: 0 });
      const controller = new AbortController();
      controllers.set(job.itemId, controller);
      try {
        const saved = await saveOne(job.config, job.source, (pct) => patch(job.itemId, { progress: pct }), controller.signal);
        // Narrative / interaction audio and documents keep working on the server
        // after the upload; follow them to "ready" or the reason they failed.
        if (saved.status === "completed") patch(job.itemId, { status: "done", progress: 100, fileId: saved.id, after: "ready" });
        else patch(job.itemId, { status: "processing", progress: 100, fileId: saved.id });
        startProcessingPoll();
      } catch (error: any) {
        // Stopped by the officer: cancelLibraryUpload already removed the item.
        if (!controller.signal.aborted) {
          console.error(`Failed to save ${job.config.file.name} to Files:`, error);
          patch(job.itemId, { status: "error", error: error?.response?.data?.error || "Upload failed" });
        }
      } finally {
        controllers.delete(job.itemId);
      }
      // Show each file in the list as soon as it lands.
      window.dispatchEvent(new Event(FILES_CHANGED_EVENT));
    }
  } finally {
    running = false;
    window.removeEventListener("beforeunload", warnBeforeLeaving);
  }
}

export function saveFilesToLibrary(configs: FileConfig[], source: "upload" | "recording" = "upload"): void {
  const toSave = configs.filter((c) => !c.previousUpload && !c.axonSource);
  if (toSave.length === 0) return;

  const added = toSave.map((config) => ({
    job: {
      itemId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      config,
      // Recordings added through the upload modal are named recording-*.wav.
      source: config.file.name.startsWith("recording-") ? ("recording" as const) : source,
    },
  }));
  setItems([
    ...items,
    ...added.map(({ job }) => ({
      id: job.itemId,
      name: job.config.displayName?.trim() || job.config.file.name,
      status: "queued" as const,
      progress: 0,
      after: afterLabel(job.config),
    })),
  ]);
  pending.push(...added.map(({ job }) => job));
  void pump();
}
